import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Select,
  Button,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Progress,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Grid,
  GridItem,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Divider,
  Avatar,
  Flex,
  Icon,
  Tooltip,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react';
import { FiUser, FiCpu, FiActivity, FiDollarSign, FiPlay, FiPause, FiCheck, FiX } from 'react-icons/fi';
import { useFindMany, useAction, useGlobalAction } from '@gadgetinc/react';
import { api } from '../api';
import { Link } from 'react-router-dom';

export default function AgentFineTuning() {
  const [selectedAgent, setSelectedAgent] = useState('all');
  const [baseModel, setBaseModel] = useState('gpt-4o-mini');
  const [epochs, setEpochs] = useState(3);
  const [batchSize, setBatchSize] = useState(1);
  const [learningRateMultiplier, setLearningRateMultiplier] = useState(2);
  const [costEstimate, setCostEstimate] = useState(0);
  
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Fetch training data
  const [{ data: trainingData, fetching: fetchingTrainingData, error: trainingDataError }] = useFindMany(api.aiTrainingData, {
    filter: {
      trainingCategory: { in: ['good_example', 'needs_improvement'] },
      isActive: { equals: true }
    },
    select: {
      id: true,
      assistantUsed: { id: true, name: true, agentType: true },
      qualityScore: true,
      trainingCategory: true,
      trainingTokens: true,
      timestamp: true,
      userInput: { markdown: true },
      aiResponse: { markdown: true }
    }
  });

  // Fetch agent configurations
  const [{ data: agentConfigs, fetching: fetchingConfigs }] = useFindMany(api.agentConfiguration, {
    select: {
      id: true,
      name: true,
      agentType: true,
      description: true,
      isActive: true,
      modelSettings: true
    }
  });

  // Create fine-tuning job action
  const [{ data: jobResult, fetching: creatingJob, error: jobError }, createJob] = useGlobalAction(api.createAgentFineTuningJob);

  // Monitor fine-tuning jobs action
  const [{ data: jobStatus, fetching: monitoringJobs }, monitorJobs] = useGlobalAction(api.monitorFineTuningJobs);

  // Agent type mapping
  const agentTypes = {
    don: { name: 'DON', avatar: '🩺', color: 'blue' },
    maia: { name: 'MaIA', avatar: '🤖', color: 'purple' },
    gerald: { name: 'Gerald', avatar: '📚', color: 'green' },
    all: { name: 'All Agents', avatar: '⭐', color: 'gray' }
  };

  // Filter training data by selected agent
  const filteredTrainingData = trainingData?.filter(item => {
    if (selectedAgent === 'all') return true;
    return item.assistantUsed?.agentType === selectedAgent;
  }) || [];

  // Calculate training statistics
  const trainingStats = {
    total: filteredTrainingData.length,
    goodExamples: filteredTrainingData.filter(item => item.trainingCategory === 'good_example').length,
    needsImprovement: filteredTrainingData.filter(item => item.trainingCategory === 'needs_improvement').length,
    averageQuality: filteredTrainingData.reduce((acc, item) => acc + (item.qualityScore || 0), 0) / filteredTrainingData.length,
    totalTokens: filteredTrainingData.reduce((acc, item) => acc + (item.trainingTokens || 0), 0)
  };

  // Calculate cost estimate
  useEffect(() => {
    if (trainingStats.totalTokens > 0) {
      // OpenAI fine-tuning costs (approximate)
      const baseCostPer1KTokens = baseModel === 'gpt-4o' ? 0.025 : 0.008;
      const trainingCostPer1KTokens = baseCostPer1KTokens * epochs;
      const estimatedCost = (trainingStats.totalTokens / 1000) * trainingCostPer1KTokens;
      setCostEstimate(estimatedCost);
    }
  }, [trainingStats.totalTokens, epochs, baseModel]);

  const handleCreateJob = async () => {
    if (trainingStats.total < 10) {
      toast({
        title: 'Insufficient Training Data',
        description: 'At least 10 training examples are required for fine-tuning.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (costEstimate > 100) {
      onOpen(); // Show cost confirmation modal
      return;
    }

    await executeJobCreation();
  };

  const executeJobCreation = async () => {
    try {
      await createJob({
        agentType: selectedAgent,
        baseModel,
        hyperparameters: {
          n_epochs: epochs,
          batch_size: batchSize,
          learning_rate_multiplier: learningRateMultiplier
        },
        trainingDataFilter: {
          agentType: selectedAgent === 'all' ? undefined : selectedAgent,
          minQualityScore: 3
        }
      });

      toast({
        title: 'Fine-tuning Job Created',
        description: 'The fine-tuning job has been successfully created and will begin processing.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      onClose();
    } catch (error) {
      toast({
        title: 'Job Creation Failed',
        description: error.message || 'Failed to create fine-tuning job.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending_batch': return 'yellow';
      case 'queued_for_upload': return 'orange';
      case 'uploading': return 'blue';
      case 'training': return 'purple';
      case 'completed': return 'green';
      case 'failed': return 'red';
      case 'cancelled': return 'gray';
      default: return 'gray';
    }
  };

  const getStatusProgress = (status) => {
    switch (status) {
      case 'pending_batch': return 10;
      case 'queued_for_upload': return 25;
      case 'uploading': return 50;
      case 'training': return 75;
      case 'completed': return 100;
      case 'failed': return 0;
      case 'cancelled': return 0;
      default: return 0;
    }
  };

  return (
    <Box p={6} maxW="1400px" mx="auto">
      {/* Back to Admin Button */}
      <Box mb={4}>
        <Link 
          to="/admin" 
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: '#3b82f6',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            textDecoration: 'none',
            fontWeight: '600',
            fontSize: '16px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
          }}
          onMouseOver={(e) => {
            e.target.style.background = '#2563eb';
            e.target.style.transform = 'translateY(-1px)';
            e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2)';
          }}
          onMouseOut={(e) => {
            e.target.style.background = '#3b82f6';
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
          }}
        >
          <span style={{ fontSize: '18px' }}>←</span>
          Back to Admin Dashboard
        </Link>
      </Box>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box>
          <Heading size="lg" mb={2}>Agent Fine-Tuning</Heading>
          <Text color="gray.600">Create and manage fine-tuning jobs for AI agents</Text>
        </Box>

        {/* Configuration Panel */}
        <Card>
          <CardHeader>
            <Heading size="md">Fine-Tuning Configuration</Heading>
          </CardHeader>
          <CardBody>
            <Grid templateColumns="repeat(auto-fit, minmax(300px, 1fr))" gap={6}>
              {/* Agent Selection */}
              <GridItem>
                <FormControl>
                  <FormLabel>Select Agent</FormLabel>
                  <Select value={selectedAgent} onChange={(e) => setSelectedAgent(e.target.value)}>
                    <option value="all">All Agents</option>
                    <option value="don">DON (Medical Assistant)</option>
                    <option value="maia">MaIA (Administrative Assistant)</option>
                    <option value="gerald">Gerald (Educational Assistant)</option>
                  </Select>
                </FormControl>
              </GridItem>

              {/* Base Model Selection */}
              <GridItem>
                <FormControl>
                  <FormLabel>Base Model</FormLabel>
                  <Select value={baseModel} onChange={(e) => setBaseModel(e.target.value)}>
                    <option value="gpt-4o-mini">GPT-4o Mini (Cost Effective)</option>
                    <option value="gpt-4o">GPT-4o (High Performance)</option>
                  </Select>
                </FormControl>
              </GridItem>

              {/* Hyperparameters */}
              <GridItem>
                <VStack spacing={4}>
                  <FormControl>
                    <FormLabel>Epochs</FormLabel>
                    <NumberInput value={epochs} onChange={(value) => setEpochs(parseInt(value))} min={1} max={10}>
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Batch Size</FormLabel>
                    <NumberInput value={batchSize} onChange={(value) => setBatchSize(parseInt(value))} min={1} max={256}>
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                </VStack>
              </GridItem>
            </Grid>
          </CardBody>
        </Card>

        {/* Training Data Overview */}
        <Grid templateColumns="repeat(auto-fit, minmax(250px, 1fr))" gap={4}>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Total Training Examples</StatLabel>
                <StatNumber>{trainingStats.total}</StatNumber>
                <StatHelpText>
                  <HStack>
                    <Text fontSize="sm">{agentTypes[selectedAgent]?.avatar}</Text>
                    <Text>{agentTypes[selectedAgent]?.name}</Text>
                  </HStack>
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Quality Score</StatLabel>
                <StatNumber>{trainingStats.averageQuality?.toFixed(1) || 'N/A'}</StatNumber>
                <StatHelpText>Average rating out of 5</StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Training Tokens</StatLabel>
                <StatNumber>{trainingStats.totalTokens.toLocaleString()}</StatNumber>
                <StatHelpText>Total tokens for training</StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Estimated Cost</StatLabel>
                <StatNumber color={costEstimate > 50 ? 'red.500' : 'green.500'}>
                  ${costEstimate.toFixed(2)}
                </StatNumber>
                <StatHelpText>USD for fine-tuning</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </Grid>

        {/* Data Quality Breakdown */}
        <Card>
          <CardHeader>
            <Heading size="md">Training Data Quality</Heading>
          </CardHeader>
          <CardBody>
            <HStack spacing={8}>
              <Box>
                <Badge colorScheme="green" fontSize="lg" p={2}>
                  {trainingStats.goodExamples} Good Examples
                </Badge>
              </Box>
              <Box>
                <Badge colorScheme="orange" fontSize="lg" p={2}>
                  {trainingStats.needsImprovement} Needs Improvement
                </Badge>
              </Box>
            </HStack>
          </CardBody>
        </Card>

        {/* Action Buttons */}
        <HStack spacing={4}>
          <Button
            colorScheme="blue"
            size="lg"
            leftIcon={<Icon as={FiPlay} />}
            onClick={handleCreateJob}
            isLoading={creatingJob}
            loadingText="Creating Job..."
            isDisabled={trainingStats.total < 10}
          >
            Start Fine-Tuning Job
          </Button>
          
          <Button
            variant="outline"
            size="lg"
            leftIcon={<Icon as={FiActivity} />}
            onClick={monitorJobs}
            isLoading={monitoringJobs}
            loadingText="Refreshing..."
          >
            Refresh Job Status
          </Button>
        </HStack>

        {/* Warnings */}
        {trainingStats.total < 10 && (
          <Alert status="warning">
            <AlertIcon />
            <AlertTitle>Insufficient Training Data!</AlertTitle>
            <AlertDescription>
              You need at least 10 training examples to create a fine-tuning job. Current count: {trainingStats.total}
            </AlertDescription>
          </Alert>
        )}

        {costEstimate > 100 && (
          <Alert status="error">
            <AlertIcon />
            <AlertTitle>High Cost Warning!</AlertTitle>
            <AlertDescription>
              The estimated cost for this fine-tuning job is ${costEstimate.toFixed(2)}. Please review before proceeding.
            </AlertDescription>
          </Alert>
        )}

        {/* Active Jobs Panel */}
        {jobStatus?.activeJobs && jobStatus.activeJobs.length > 0 && (
          <Card>
            <CardHeader>
              <Heading size="md">Active Fine-Tuning Jobs</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={4}>
                {jobStatus.activeJobs.map((job, index) => (
                  <Box key={index} w="100%" p={4} borderWidth={1} borderRadius="md">
                    <Flex justify="space-between" align="center" mb={2}>
                      <HStack>
                        <Avatar size="sm" name={job.agentType} />
                        <VStack align="start" spacing={0}>
                          <Text fontWeight="bold">{job.agentType} - {job.baseModel}</Text>
                          <Text fontSize="sm" color="gray.600">Job ID: {job.id}</Text>
                        </VStack>
                      </HStack>
                      <Badge colorScheme={getStatusColor(job.status)}>
                        {job.status}
                      </Badge>
                    </Flex>
                    <Progress 
                      value={getStatusProgress(job.status)} 
                      colorScheme={getStatusColor(job.status)}
                      mb={2}
                    />
                    <Text fontSize="sm" color="gray.600">
                      Started: {new Date(job.startedAt).toLocaleString()}
                    </Text>
                  </Box>
                ))}
              </VStack>
            </CardBody>
          </Card>
        )}

        {/* Cost Confirmation Modal */}
        <Modal isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>High Cost Warning</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Alert status="warning" mb={4}>
                <AlertIcon />
                <VStack align="start">
                  <AlertTitle>This fine-tuning job will cost approximately ${costEstimate.toFixed(2)}</AlertTitle>
                  <AlertDescription>
                    This is higher than the recommended threshold of $100. Are you sure you want to proceed?
                  </AlertDescription>
                </VStack>
              </Alert>
              
              <TableContainer>
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>Item</Th>
                      <Th>Value</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    <Tr>
                      <Td>Training Examples</Td>
                      <Td>{trainingStats.total}</Td>
                    </Tr>
                    <Tr>
                      <Td>Total Tokens</Td>
                      <Td>{trainingStats.totalTokens.toLocaleString()}</Td>
                    </Tr>
                    <Tr>
                      <Td>Base Model</Td>
                      <Td>{baseModel}</Td>
                    </Tr>
                    <Tr>
                      <Td>Epochs</Td>
                      <Td>{epochs}</Td>
                    </Tr>
                  </Tbody>
                </Table>
              </TableContainer>
            </ModalBody>

            <ModalFooter>
              <Button colorScheme="gray" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button 
                colorScheme="red" 
                onClick={executeJobCreation}
                isLoading={creatingJob}
                loadingText="Creating..."
              >
                Proceed with Fine-Tuning
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </VStack>
    </Box>
  );
}